import api from '@/api/api.ts';
import { useApiGet } from '@/api/apiRequests';
import { getParagraphs } from '@/api/services';
import { getFilters } from '@/api/services/getFilters';
import { getMetrics } from '@/api/services/getMetrics';
import { PageLoader } from '@/components/PageLoader';
import { FiltersType } from '@/interfaces/Filters.ts';
import { ParagraphItem } from '@/interfaces/Paragraphs';
import { AxiosError } from 'axios';
import { ChartNetwork, EditIcon, FileSpreadsheet, Trash2Icon, TrendingUpDown } from 'lucide-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

export const Paragraphs: FC = () => {
  const [tableData, setTableData] = useState();
  const [filters, setFilters] = useState<FiltersType>();
  const [metrics, setMetrics] = useState();
  const [itemDialog, setItemDialog] = useState<boolean>(false);
  const emptyItem: ParagraphItem = {
    name: '',
    category: '',
    // biome-ignore lint/style/useNamingConvention: <explanation>
    input_type: '',
    inputs: [],
  };
  const [item, setItem] = useState<ParagraphItem>(emptyItem);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
  const [paragraphToDelete, setParagraphToDelete] = useState<number | null>(null);
  const { data, isLoading, refetch } = useApiGet<any, AxiosError>(['paragraphs'], async () => await getParagraphs(), {
    enabled: true,
  });
  const { data: filterData } = useApiGet<any, AxiosError>(['filters'], async () => await getFilters(), {
    enabled: true,
  });
  const { data: metricsData } = useApiGet<any, AxiosError>(['metrics'], async () => await getMetrics(), {
    enabled: true,
  });

  useEffect(() => {
    if (data) {
      if (categoryFilter) {
        // Filter data by selected category
        setTableData(data.filter((item: any) => item.category === categoryFilter));
      } else {
        // No filter, show all data
        setTableData(data);
      }
    }
  }, [data, categoryFilter]);

  useEffect(() => {
    setFilters(filterData);
    setMetrics(metricsData);
  }, [filterData, metricsData]);

  const handleCategoryClick = (category: string) => {
    setCategoryFilter((prevFilter) => (prevFilter === category ? null : category));
  };

  const openNew = () => {
    setItem(emptyItem);
    setIsEditMode(false);
    setItemDialog(true);
  };

  const openEdit = (rowData: ParagraphItem) => {
    let categoryValue = rowData.category;

    if (filters?.category) {
      if (!Object.keys(filters.category).includes(rowData.category)) {
        const categoryEntry = Object.entries(filters.category).find(
          // biome-ignore lint/style/useNamingConvention: <explanation>
          ([_, label]) => label === rowData.category
        );
        if (categoryEntry) {
          categoryValue = categoryEntry[0];
        }
      }
    }

    setItem({
      id: rowData.id,
      name: rowData.name,
      category: categoryValue,
      // biome-ignore lint/style/useNamingConvention: <explanation>
      input_type: rowData.input_type,
      // @ts-ignore
      inputs:
        rowData.input_type === 'summary' ? rowData.inputs?.map((input) => Number(input)) || [] : rowData.inputs || [],
    });
    setIsEditMode(true);
    setItemDialog(true);
  };

  const hideDialog = () => {
    setItem(emptyItem);
    setIsEditMode(false);
    setItemDialog(false);
  };

  const saveItem = async () => {
    if (!item.name || !item.category || !item.input_type) {
      toast.error('Fill in all fields.');
      return;
    }

    try {
      let response;

      if (isEditMode && item.id) {
        // Update existing paragraph
        response = await api.put(`/paragraph/${item.id}`, {
          name: item.name,
          category: item.category,
          // biome-ignore lint/style/useNamingConvention: <explanation>
          input_type: item.input_type,
          inputs: item.input_type === 'summary' ? item.inputs.map((id) => String(id)) : item.inputs,
        });
      } else {
        // Create new paragraph
        response = await api.post(`/paragraph/${item.input_type}`, {
          name: item.name,
          category: item.category,
          inputs: item.input_type === 'summary' ? item.inputs.map((id) => String(id)) : item.inputs,
        });
      }

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        toast.success(isEditMode ? 'The record has been successfully edited.' : 'The record was successfully saved.');
        await refetch();
        setItemDialog(false);
        setItem(emptyItem);
        setIsEditMode(false);
      }
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Error when saving a record.');
      }
      console.error('Error saving item:', error);
    }
  };

  const itemDialogFooter = (
    <>
      <Button className={'btn-secondary'} style={{ borderRadius: '6px' }} label="Cancel" outlined={true} onClick={hideDialog} />
      <Button className={'btn-primary'} style={{ borderRadius: '6px' }} label="Save" onClick={saveItem} />
    </>
  );

  const getMetricDescription = (metricCode: string, metricsData: any[]): string => {
    if (!metricsData || !Array.isArray(metricsData)) return metricCode;

    const metric = metricsData.find((m) => m.metric_code === metricCode);
    if (metric && metric.description) {
      return metric.description;
    }
    return metricCode;
  };

  const confirmDelete = (paragraphId: number) => {
    setParagraphToDelete(paragraphId);
    setDeleteDialogVisible(true);
  };

  const handleDeleteParagraph = async () => {
    if (!paragraphToDelete) return;

    try {
      const response = await api.delete(`/paragraph/${paragraphToDelete}`);

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        toast.success('Paragaraph has been successfully deleted.');
        await refetch();
        setDeleteDialogVisible(false);
        setParagraphToDelete(null);
      }
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Error when deleting a record.');
      }
      console.error('Error deleting item:', error);
    }
  };

  const actionBodyTemplate = (rowData: any) => {
    return (
      <div className="flex gap-1">
        <Button
          icon={<EditIcon size={16} />}
          className="ml-auto p-button-text p-button-info"
          onClick={() => openEdit(rowData)}
        />
        <Button
          icon={<Trash2Icon size={16} />}
          className="ml-auto p-button-text p-button-danger"
          onClick={() => confirmDelete(rowData.id)}
        />
      </div>
    );
  };

  const hasNoAvailableInputs = () => {
    if (item.input_type !== 'summary' || !item.category) return false;
    // @ts-ignore
    const categoryLabel = filters?.category?.[item.category];
    // @ts-ignore
    const filteredData = tableData?.filter((p: any) => {
      return (
        (p.category === item.category || p.category === categoryLabel) && p.id !== item.id && p.input_type !== 'summary'
      );
    });
    return !filteredData || filteredData.length === 0;
  };

  const renderParagraphTypeButton = (inputType: string) => {
    type ButtonSeverity = 'secondary' | 'success' | 'warning' | 'info' | 'danger' | 'help' | 'contrast';

    const iconProps: Record<string, { icon: React.ReactNode; severity: ButtonSeverity }> = {
      summary: { icon: <FileSpreadsheet size={17} />, severity: 'secondary' },
      prediction: { icon: <TrendingUpDown size={18} />, severity: 'info' },
      default: { icon: <ChartNetwork size={18} />, severity: 'warning' },
    };

    const config = iconProps[inputType] || iconProps.default;

    return (
      <Button
        rounded={true}
        icon={config.icon}
        severity={config.severity}
        size={'small'}
        tooltip={inputType}
        tooltipOptions={{ position: 'top' }}
        className={'!py-1 !px-2 !w-9 !h-9'}
      />
    );
  };

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>Paragraphs</h1>
      <div className={'flex justify-end gap-2 mb-2'}>
        {categoryFilter && (
          <Button
            label={`Clear filter: ${categoryFilter}`}
            size={'small'}
            severity={'danger'}
            outlined={true}
            style={{ borderRadius: '6px' }}
            onClick={() => setCategoryFilter(null)}
          />
        )}
        <Button label="Add paragraph" className={'btn-primary'} style={{ borderRadius: '6px' }} onClick={openNew} />
      </div>
      <DataTable
        value={tableData}
        removableSort={true}
        showGridlines={true}
        paginator={true}
        size={'small'}
        sortField={'id'}
        sortOrder={1}
        rows={20}
        tableStyle={{ minWidth: '50rem', width: '100%' }}
      >
        <Column field="name" header="Name" sortable={true} />
        <Column
          field="category"
          header="Category"
          sortable={true}
          body={(rowData: any) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
            <span
              className="cursor-pointer hover:text-blue-600 hover:underline"
              onClick={() => handleCategoryClick(rowData.category)}
            >
              {rowData.category}
            </span>
          )}
        />
        <Column
          field="input_type"
          header="Paragraph type"
          sortable={true}
          align={'center'}
          body={(rowData: any) => renderParagraphTypeButton(rowData.input_type)}
        />
        <Column
          field="inputs"
          header="Inputs"
          className={'text-sm'}
          body={(rowData: any) => {
            if (rowData.input_type === 'summary' && rowData.inputs?.length > 0) {
              return (
                <div className="flex flex-wrap gap-1">
                  {rowData.inputs?.map((inputId: number, index: number) => {
                    // @ts-ignore
                    const paragraph = tableData?.find((p: any) => Number(p.id) === Number(inputId));
                    return paragraph ? (
                      <span
                        key={`tag-${inputId + index}`}
                        className="px-2 py-1 font-medium text-xs bg-green-100 text-green-800 rounded-full"
                      >
                        {paragraph.name}
                      </span>
                    ) : null;
                  })}
                </div>
              );
              // biome-ignore lint/style/noUselessElse: <explanation>
            } else {
              return (
                <div className="flex flex-wrap gap-1">
                  {rowData.inputs?.map((input: string, index: number) => (
                    <Button
                      rounded={true}
                      key={index + input}
                      size={'small'}
                      tooltip={getMetricDescription(input, metricsData)}
                      tooltipOptions={{ position: 'top' }}
                      className={'!text-xs !py-1 !px-2'}
                    >
                      {input}
                    </Button>
                  ))}
                </div>
              );
            }
          }}
        />
        <Column align={'center'} style={{ width: '4rem' }} body={actionBodyTemplate} />
      </DataTable>
      <Dialog
        visible={itemDialog}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        header={isEditMode ? 'Update paragraph' : 'Add new paragraph'}
        modal={true}
        className="p-fluid"
        footer={itemDialogFooter}
        onHide={hideDialog}
        draggable={false}
      >
        <div className={'flex flex-col gap-4'}>
          <div className="field">
            <label htmlFor="category" className="font-bold">
              Category
            </label>
            <Dropdown
              value={item?.category}
              onChange={(e: DropdownChangeEvent) => setItem((prev) => ({ ...prev, category: e.value }))}
              options={
                filters?.category ? Object.entries(filters.category).map(([value, label]) => ({ label, value })) : []
              }
              optionLabel="label"
              filter={true}
              required={true}
              placeholder="Choose category"
              className="w-full"
            />
          </div>
          <div className="field">
            <label htmlFor="name" className="font-bold">
              Name
            </label>
            <InputText
              id="name"
              value={item?.name}
              onChange={(e) => setItem((prev) => ({ ...prev, name: e.target.value }))}
              required={true}
              autoFocus={true}
            />
          </div>
          <div className="field">
            <label htmlFor="paragraph_type" className="font-bold">
              Paragraph type
            </label>
            <Dropdown
              value={item?.input_type}
              id={'paragraph_type'}
              // biome-ignore lint/style/useNamingConvention: <explanation>
              onChange={(e: DropdownChangeEvent) => setItem((prev) => ({ ...prev, input_type: e.value, inputs: [] }))}
              options={
                filters?.paragraph_type
                  ? Object.entries(filters.paragraph_type).map(([value, label]) => ({ label, value }))
                  : []
              }
              optionLabel="label"
              filter={true}
              required={true}
              placeholder="Choose paragraph type"
              className="w-full"
            />
          </div>
          <div className="field">
            <label htmlFor="inputs" className="font-bold">
              Inputs
            </label>
            <MultiSelect
              value={item?.inputs}
              onChange={(e: MultiSelectChangeEvent) =>
                setItem((prev) => ({
                  ...prev,
                  inputs: e.value,
                }))
              }
              options={(() => {
                if (item.input_type === 'summary' && item.category) {
                  // @ts-ignore
                  const categoryLabel = filters?.category?.[item.category];
                  // @ts-ignore
                  const filteredData = tableData?.filter((p: any) => {
                    return (
                      (p.category === item.category || p.category === categoryLabel) &&
                      p.id !== item.id &&
                      p.input_type !== 'summary'
                    );
                  });
                  return filteredData || [];
                }
                if (item.input_type === 'prediction') {
                  return Array.isArray(metrics)
                    ? (metrics as any[]).filter((metric: any) => metric.is_prediction_possible === true)
                    : [];
                }
                return metrics;
              })()}
              optionLabel={item.input_type === 'summary' ? 'name' : 'metric_code'}
              optionValue={item.input_type === 'summary' ? 'id' : 'metric_code'}
              filter={true}
              required={true}
              placeholder="Choose inputs"
              className="w-full"
              disabled={!item.category || !item.input_type || (item.input_type === 'summary' && hasNoAvailableInputs())}
            />
            {item.input_type === 'summary' && item.category && hasNoAvailableInputs() && (
              <div className="mt-2 p-2 bg-yellow-50 text-yellow-800 text-sm rounded border border-yellow-200">
                There is no paragraph in a given category, so it cannot be summarized.
              </div>
            )}
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={deleteDialogVisible}
        style={{ width: '450px' }}
        header="Confirm deletion"
        modal={true}
        draggable={false}
        footer={
          <>
            <Button label="No" className={'btn-secondary'} style={{ borderRadius: '6px' }} onClick={() => setDeleteDialogVisible(false)} />
            <Button label="Yes" onClick={handleDeleteParagraph} className="btn-primary" style={{ borderRadius: '6px' }} />
          </>
        }
        onHide={() => setDeleteDialogVisible(false)}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>Do you really want to delete this paragraph?</span>
        </div>
      </Dialog>
      <PageLoader visible={isLoading} />
    </>
  );
};
